import { useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import {
  useCarrierAddOnServiceEnablement,
  useUpdateCarrierAddOnServiceEnablement,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { hasHttpStatus } from "@/lib/api-error";

export function PickupServiceEnablement({ serviceId }: { serviceId: string }) {
  const enablement = useCarrierAddOnServiceEnablement(serviceId);
  const update = useUpdateCarrierAddOnServiceEnablement();
  const rows = enablement.data ?? [];
  const [error, setError] = useState<string | null>(null);
  const [pendingCarrierId, setPendingCarrierId] = useState<string | null>(null);
  const submissionLock = useRef(false);
  const forbidden = hasHttpStatus(enablement.error, 403);

  const toggle = async (labelCarrierId: string, enabled: boolean) => {
    if (submissionLock.current) return;
    setError(null);
    submissionLock.current = true;
    setPendingCarrierId(labelCarrierId);
    try {
      await update.mutateAsync({ serviceId, labelCarrierId, enabled });
      toast.success(
        enabled ? "Label carrier enabled." : "Label carrier disabled."
      );
    } catch (caught) {
      const response = caught as { response?: { data?: { error?: string } } };
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to update label carrier enablement."
        : (response.response?.data?.error ??
          "Enablement could not be updated.");
      setError(message);
      toast.error(message);
    } finally {
      submissionLock.current = false;
      setPendingCarrierId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Label carrier enablement</CardTitle>
        <p className="text-sm text-muted-foreground">
          EZX controls which label carriers this provider may serve. You can
          enable this specific pickup service for any carrier in that granted
          set.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {enablement.error && (
          <div
            role="alert"
            className="flex flex-col gap-2 rounded-md border bg-destructive/5 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
          >
            <span>
              {forbidden
                ? "You do not have permission to view label carrier enablement."
                : "Label carrier enablement could not be loaded."}
            </span>
            {!forbidden && (
              <Button
                variant="outline"
                size="sm-touch"
                onClick={() => void enablement.refetch()}
              >
                Try again
              </Button>
            )}
          </div>
        )}

        {!forbidden && (!enablement.error || rows.length > 0) && (
          <div className="space-y-2 md:hidden">
            {enablement.isLoading ? (
              <div role="status" className="p-6 text-center text-sm">
                Loading enablement…
              </div>
            ) : rows.length === 0 ? (
              <EnablementEmptyState />
            ) : (
              rows.map((row) => (
                <div
                  key={row.labelCarrierId}
                  className="flex min-h-16 items-center gap-3 rounded-md border p-3"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {row.labelCarrier.name}
                    </span>
                    <code className="text-xs text-muted-foreground">
                      {row.labelCarrier.code}
                    </code>
                  </span>
                  <StatusBadge status={row.enabled ? "ENABLED" : "DISABLED"} />
                  <Button
                    size="sm-touch"
                    variant={row.enabled ? "outline" : "default"}
                    aria-label={`${row.enabled ? "Disable" : "Enable"} ${row.labelCarrier.name}`}
                    disabled={update.isPending || pendingCarrierId !== null}
                    onClick={() =>
                      void toggle(row.labelCarrierId, !row.enabled)
                    }
                  >
                    {pendingCarrierId === row.labelCarrierId
                      ? "Saving…"
                      : row.enabled
                        ? "Disable"
                        : "Enable"}
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {!forbidden && (!enablement.error || rows.length > 0) && (
          <div className="hidden overflow-hidden rounded-md border md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label carrier</TableHead>
                  <TableHead>Availability</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enablement.isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="py-10 text-center text-muted-foreground"
                    >
                      Loading enablement…
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="py-10 text-center">
                      <ShieldCheck className="mx-auto mb-2 h-7 w-7 text-muted-foreground" />
                      <p className="font-medium">No label carriers granted.</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Contact EZX to grant pickup access before this service
                        can be offered.
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.labelCarrierId}>
                      <TableCell>
                        <div className="font-medium">
                          {row.labelCarrier.name}
                        </div>
                        <code className="text-xs text-muted-foreground">
                          {row.labelCarrier.code}
                        </code>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={row.enabled ? "ENABLED" : "DISABLED"}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant={row.enabled ? "outline" : "default"}
                          disabled={
                            update.isPending || pendingCarrierId !== null
                          }
                          onClick={() =>
                            void toggle(row.labelCarrierId, !row.enabled)
                          }
                        >
                          {pendingCarrierId === row.labelCarrierId
                            ? "Saving…"
                            : row.enabled
                              ? "Disable"
                              : "Enable"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EnablementEmptyState() {
  return (
    <div className="p-6 text-center">
      <ShieldCheck className="mx-auto mb-2 h-7 w-7 text-muted-foreground" />
      <p className="font-medium">No label carriers granted.</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Contact EZX to grant pickup access before this service can be offered.
      </p>
    </div>
  );
}
